<template>
  <div data-page-name="guides">
    <div class="wrapper">
      <section class="hero">
        <h1>{{ $t('Storage_Guides') }}</h1>

        <p v-html="$t('guides.hero.description')" />
      </section>

      <section v-if="mainPreview" class="most-reviewed">
        <div class="left">
          <NuxtLink :to="createPreviewLink(mainPreview)">
            <h2>{{ mainPreview.data?.title }}</h2>
          </NuxtLink>

          <Buttons.Link
            :to="createPreviewLink(mainPreview)"
            fill="none"
            accent="black"
          >
            {{ $t('Read_the_Article') }}
            <SvgIcon name="arrow-forward" fill="inherit" />
          </Buttons.Link>
        </div>

        <div
          v-if="
            getPreviewType(mainPreview) === 'WITH_PICTURE' &&
            typeof getPreviewImage(mainPreview) === 'string'
          "
          class="right"
        >
          <AppTag variant="image">
            {{ getPreviewCategorySlug(mainPreview) }}
          </AppTag>

          <AppTag variant="orange">
            <SvgIcon name="eye" />
            {{ $t('Most_viewed') }}
          </AppTag>

          <NuxtLink :to="createPreviewLink(mainPreview)">
            <NuxtImg
              :src="getPreviewImage(mainPreview)"
              alt=""
              width="592"
              height="405"
              densities="x1 x2"
              format="webp"
              loading="lazy"
            />
          </NuxtLink>
        </div>
      </section>

      <hr />

      <div class="content" ref="contentRef">
        <div class="categories">
          <strong>{{ $t('Categories') }}:</strong>

          <ul>
            <li v-for="category of categories" :key="category.id">
              <AppCheckbox
                :id="category.slug"
                :model-value="state.filters.has(category.slug)"
                @update:model-value="toggleFilter(category.slug, $event)"
              />

              <label :for="category.slug">
                {{ category.data?.title }}
              </label>
            </li>
          </ul>
        </div>

        <AppSubscribe />

        <div class="cta-block">
          <strong>{{ $t('guides.cta-block.title') }}</strong>

          <Buttons.Link
            :to="localePathWithSlash(PATHS.REQUEST_DEMO.path)"
            fill="outline"
            accent="white"
          >
            {{ $t('Request_a_Demo') }}
            <SvgIcon name="arrow-forward" />
          </Buttons.Link>
        </div>

        <div class="posts">
          <CardResource
            v-for="postPreview of postsPreviews"
            :key="postPreview.id"
            v-bind="toCardResourceProps(postPreview)"
          />

          <AppPagination
            v-if="Number(postsPreviewsMeta?.lastPage) > 1"
            v-model="state.page"
            :total-items="Number(postsPreviewsMeta?.total)"
            :items-per-page="PER_PAGE"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// components
import AppTag from '~/components/redesign/AppTag.vue';
import Buttons from '~/components/redesign/Buttons';
import AppCheckbox from '~/components/redesign/AppCheckbox.vue';
import AppSubscribe from '~/components/AppSubscribe.vue';
import CardResource from '~/components/redesign/CardResource.vue';
import AppPagination from '~/components/redesign/AppPagination.vue';

// composables
import useApiSeo from '~/composables/useApiSeo';
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// constants
import { PATHS } from '~/constants/nav-items';
import { PostType } from '~/types';

// utils
import debounce from 'lodash.debounce';
import scrollToElement from '~/utils/scrollToElement';

// types
import type { CardResourceProps } from '~/components/redesign/CardResource.vue';
import type { BlogArticle } from '~/types/blog';

const PER_PAGE = 6;

const { t } = useI18n();
const router = useRouter();
const localeRoute = useLocaleRoute();
const { searchParams } = useRequestURL();
const localePathWithSlash = useLocalePathWithSlash();
const GqlInstance = useGql();

const contentRef = ref<null | HTMLElement>(null);

const state = reactive({
  page: Number(searchParams.get('page')) || 1,
  filters: new Set<string>(searchParams.getAll('filters')),
});

const { error: mainPreviewError, data: mainPreview } = await useAsyncGql({
  operation: 'BlogArticle',
  variables: {
    slug: 'ransomware-response-plan',
  },
  options: {
    transform: data => data.blog_article,
  },
});

const { error: categoriesError, data: categories } = await useAsyncGql({
  operation: 'Categories',
  variables: {
    type: PostType.GUIDE,
  },
  options: {
    transform: data => data.blog_categories.data,
  },
});

const {
  error: postsPreviewsError,
  data: postsPreviewsData,
  refresh: refreshPreviewsData,
} = await useAsyncData(
  'BlogArticlesPreviews',
  () =>
    GqlInstance(
      state.filters.size > 0
        ? 'BlogArticlesPreviewsFiltered'
        : 'BlogArticlesPreviews',
      {
        page: state.page,
        types: PostType.GUIDE,
        first: PER_PAGE,
        category_slugs: Array.from(state.filters),
      },
    ),
  {
    transform: data => data.blog_articles,
  },
);

watch(state, ({ page, filters }) => {
  const route = localeRoute({
    path: PATHS.GUIDES.path,
    query: {
      page,
      filters: Array.from(filters),
    },
  });

  if (route) {
    router.push(route);
  }
});

watch(
  () => state.page,
  debounce(async () => {
    await refreshPreviewsData();
    scrollToContentStart();
  }, 250),
);

watch(
  state.filters,
  debounce(async () => {
    if (state.page === 1) {
      await refreshPreviewsData();
    } else {
      state.page = 1;
    }
  }, 500),
);

const postsPreviews = computed(() => postsPreviewsData.value?.data);
const postsPreviewsMeta = computed(
  () => postsPreviewsData.value?.paginatorInfo,
);

const toggleFilter = (slug: string, value: boolean) => {
  state.filters[value ? 'add' : 'delete'](slug);
};

const getPreviewCategorySlug = (preview: BlogArticle) => {
  return preview.categories?.[0].slug;
};

const getPreviewType = (preview: BlogArticle) =>
  preview.data?.data?.preview_type;

const getPreviewImage = (preview: BlogArticle) =>
  preview.data?.data?.preview_picture_url;

const createPreviewLink = (preview: BlogArticle): string => {
  const { slug } = preview;
  const category = getPreviewCategorySlug(preview);

  return localePathWithSlash(`${PATHS.GUIDES.path}${category}/${slug}`);
};

const toCardResourceProps = (preview: BlogArticle) => {
  const {
    data: { title, data },
  } = preview;

  const props: CardResourceProps = {
    title,
    tags: [
      {
        text: getPreviewCategorySlug(preview)!,
        variant: 'image',
      },
    ],
    link: {
      href: createPreviewLink(preview),
      text: t('Read_the_Article'),
    },
  };

  if (data?.preview_picture_url) {
    props.image = {
      src: data.preview_picture_url,
      alt: 'image description', // todo
    };
  }

  return props;
};

const scrollToContentStart = () => {
  if (!contentRef.value) return;

  scrollToElement(contentRef.value, { block: 'start' });
};

useApiSeo('guides');
</script>

<style lang="scss">
body.violet .content ul li {
  padding-left: 0;

  &::before {
    display: none;
  }
}
</style>

<style scoped lang="scss">
@import '$/mixins/size';
@import '$/mixins/flex';
@import '$/mixins/media';
@import '$/mixins/typography';
@import '$/functions/token';

[data-page-name='guides'] {
  padding: 8rem 0;

  @include tablet {
    padding: 5rem 0;
  }

  @include mobile {
    padding: 2.5rem 0;
  }

  .wrapper {
    padding: 0 2rem;
    @include wrapper(80rem);

    @include mobile {
      padding: 0 1rem;
    }

    section.hero {
      margin-bottom: 5rem;

      @include mobile {
        margin-bottom: 2.5rem;
      }

      h1 {
        @include h2;
        margin-bottom: 2rem;

        @include tablet {
          margin-bottom: 1.5rem;
        }
      }

      &:deep(p) {
        @include body-2;

        br {
          @include tablet {
            display: none;
          }
        }
      }
    }

    section.most-reviewed {
      @include flex-center-sb;
      gap: 0 2rem;

      margin-bottom: 4.5rem;

      @include tablet {
        margin-bottom: 5rem;
      }

      @include mobile {
        gap: 1.5rem 0;
        flex-direction: column-reverse;
        margin-bottom: 2.5rem;
      }

      .left,
      .right {
        width: 50%;

        @include mobile {
          width: 100%;
        }
      }

      .left {
        h2 {
          @include h3;
          margin-bottom: 1rem;
        }

        [data-component-name='Buttons.Link'] {
          display: block;
          width: max-content;

          @include mobile {
            width: 100%;
          }
        }
      }

      .right {
        position: relative;

        [data-component-name='AppTag'] {
          position: absolute;
          top: 1rem;
          width: max-content;

          &:first-of-type {
            left: 1rem;
            text-transform: capitalize;
          }

          &:last-of-type {
            right: 1rem;

            @include body-3;
            @include flex-center-sb;
            gap: 0.25rem;
          }
        }

        img {
          width: 100%;
          height: auto;
          max-height: 25.3125rem;

          object-fit: cover;

          @include mobile {
            max-height: 14rem;
          }
        }
      }
    }

    hr {
      border: unset;

      height: 1px;
      background-color: token('outline-secondary');

      margin-bottom: 2rem;
    }

    .content {
      display: grid;
      grid-template-columns: 24rem 1fr;
      grid-gap: 2rem;

      scroll-margin-top: calc(var(--scroll-offset) + 1rem);

      @include tablet {
        grid-template-columns: 1fr;
        grid-gap: 1.5rem;
      }

      > * {
        height: min-content;
      }

      .categories {
        grid-column: 1/2;
        grid-row: 1/2;

        @include tablet {
          @include flex-start-sb;
          margin-bottom: 0.5rem;
        }

        @include mobile {
          flex-direction: column;
          margin-bottom: 0;
        }

        > * {
          @include tablet {
            width: calc(50% - 1rem);
          }
        }

        strong {
          display: block;
          @include subtitle-1;
          margin-bottom: 2rem;

          @include mobile {
            margin-bottom: 1.5rem;
          }
        }

        ul {
          @include flex-start-start;
          flex-direction: column;
          gap: 1rem 0;

          li {
            @include flex-center-start;
            gap: 0 0.25rem;

            [data-component-name='AppCheckbox'] {
            }

            label {
              cursor: pointer;
              @include body-3;
            }
          }
        }
      }

      [data-component-name='AppSubscribe'] {
        grid-column: 1/2;
        grid-row: 2/3;

        @include tablet {
          grid-row: 3/4;
        }
      }

      .cta-block {
        grid-column: 1/2;
        grid-row: 3/4;

        padding: 2rem;
        background-color: token('surf-cont-tert-action');

        @include flex-start-start;
        flex-direction: column;
        gap: 2rem 0;

        @include tablet {
          grid-row: 4/5;
          gap: 1.5rem 0;
          padding: 1.5rem;
        }

        @include mobile {
          gap: 1rem 0;
          padding: 1.5rem 1rem;
        }

        strong {
          @include subtitle-1;
          color: token('text-reversed');
        }

        [data-component-name='Buttons.Link'] {
          align-self: flex-end;

          @include mobile {
            align-self: stretch;
          }
        }
      }

      .posts {
        grid-column: 2/3;
        grid-row: 1/5;

        @include flex-stretch-sb;
        flex-wrap: wrap;
        gap: 2rem 0;

        @include tablet {
          grid-column: 1/2;
          grid-row: 2/3;

          gap: 1.5rem 0;
        }

        [data-component-name='CardResource'] {
          width: calc(50% - 1rem);

          @include tablet {
            width: calc(50% - 0.75rem);
          }

          @include mobile {
            width: 100%;
          }
        }

        [data-component-name='AppPagination'] {
          width: 100%;
          margin-top: 3rem;

          @include tablet {
            margin-top: 3.5rem;
          }

          @include mobile {
            margin-top: 1rem;
          }
        }
      }
    }
  }
}
</style>
